import { isKernelEventType, isQueryName, } from "@econ-v1/domain";
import { deserializeClientError, isJsonObject, isSerializableClientError, serializeClientError, } from "./errors.js";
export function serializeRpcRequest(request) {
    switch (request.type) {
        case "command":
            return stringify({ id: request.id, name: request.name, payload: request.payload, type: request.type });
        case "query":
            return stringify({ id: request.id, name: request.name, params: request.params, type: request.type });
        case "live-query.subscribe":
            return stringify({ id: request.id, name: request.name, params: request.params, subscriptionId: request.subscriptionId, type: request.type });
        case "live-query.unsubscribe":
        case "event.unsubscribe":
            return stringify({ id: request.id, subscriptionId: request.subscriptionId, type: request.type });
        case "event.subscribe":
            return stringify({ eventType: request.eventType, id: request.id, subscriptionId: request.subscriptionId, type: request.type });
        case "event.publish":
            return stringify({ event: projectDomainEvent(request.event), id: request.id, type: request.type });
        case "stage.prepare":
            return stringify({ appName: request.appName, id: request.id, type: request.type });
        case "stage.open-session":
            return stringify({ appName: request.appName, id: request.id, manifestSha256: request.manifestSha256, type: request.type });
        case "stage.query":
            return stringify({ id: request.id, name: request.name, params: request.params, sessionId: request.sessionId, type: request.type });
        case "stage.live-query.subscribe":
            return stringify({ id: request.id, name: request.name, params: request.params, sessionId: request.sessionId, subscriptionId: request.subscriptionId, type: request.type });
        case "stage.live-query.unsubscribe":
        case "stage.event.unsubscribe":
            return stringify({ id: request.id, sessionId: request.sessionId, subscriptionId: request.subscriptionId, type: request.type });
        case "stage.event.subscribe":
            return stringify({ eventType: request.eventType, id: request.id, sessionId: request.sessionId, subscriptionId: request.subscriptionId, type: request.type });
        case "stage.event.publish":
            return stringify({ event: projectDomainEvent(request.event), id: request.id, sessionId: request.sessionId, type: request.type });
        case "stage.capability.invoke":
            return stringify({ capability: request.capability, id: request.id, payload: request.payload, sessionId: request.sessionId, type: request.type });
        case "stage.close-session":
            return stringify({ id: request.id, sessionId: request.sessionId, type: request.type });
    }
}
export function deserializeRpcRequest(serialized) {
    const value = parseRecord(serialized);
    const type = stringField(value, "type");
    const id = stringField(value, "id");
    switch (type) {
        case "command": return { id, name: versionedNameField(value, "name"), payload: jsonField(value, "payload"), type };
        case "query": return { id, name: queryNameField(value), params: jsonField(value, "params"), type };
        case "live-query.subscribe": return { id, name: queryNameField(value), params: jsonField(value, "params"), subscriptionId: stringField(value, "subscriptionId"), type };
        case "live-query.unsubscribe": return { id, subscriptionId: stringField(value, "subscriptionId"), type };
        case "event.subscribe": return { eventType: eventTypeField(value), id, subscriptionId: stringField(value, "subscriptionId"), type };
        case "event.unsubscribe": return { id, subscriptionId: stringField(value, "subscriptionId"), type };
        case "event.publish": return { event: domainEventField(value), id, type };
        case "stage.prepare": return { appName: stringField(value, "appName"), id, type };
        case "stage.open-session": return { appName: stringField(value, "appName"), id, manifestSha256: stringField(value, "manifestSha256"), type };
        case "stage.query": return { id, name: queryNameField(value), params: jsonField(value, "params"), sessionId: stringField(value, "sessionId"), type };
        case "stage.live-query.subscribe": return { id, name: queryNameField(value), params: jsonField(value, "params"), sessionId: stringField(value, "sessionId"), subscriptionId: stringField(value, "subscriptionId"), type };
        case "stage.live-query.unsubscribe": return { id, sessionId: stringField(value, "sessionId"), subscriptionId: stringField(value, "subscriptionId"), type };
        case "stage.event.subscribe": return { eventType: eventTypeField(value), id, sessionId: stringField(value, "sessionId"), subscriptionId: stringField(value, "subscriptionId"), type };
        case "stage.event.unsubscribe": return { id, sessionId: stringField(value, "sessionId"), subscriptionId: stringField(value, "subscriptionId"), type };
        case "stage.event.publish": return { event: domainEventField(value), id, sessionId: stringField(value, "sessionId"), type };
        case "stage.capability.invoke": return { capability: stringField(value, "capability"), id, payload: jsonField(value, "payload"), sessionId: stringField(value, "sessionId"), type };
        case "stage.close-session": return { id, sessionId: stringField(value, "sessionId"), type };
        default: throw new TypeError("Unknown RPC request type");
    }
}
function versionedNameField(value, field) {
    const name = stringField(value, field);
    if (!/^[-a-z0-9.]+\.v[1-9][0-9]*$/u.test(name))
        throw new TypeError(`Expected ${field} to be versioned`);
    return name;
}
export function serializeRpcResponse(response) {
    return response.ok
        ? stringify({ id: response.id, ok: true, value: response.value })
        : stringify({ error: serializeClientError(response.error), id: response.id, ok: false });
}
export function deserializeRpcResponse(serialized) {
    const value = parseRecord(serialized);
    const id = stringField(value, "id");
    if (value.ok === true)
        return { id, ok: true, value: jsonField(value, "value") };
    if (value.ok === false && isSerializableClientError(value.error))
        return { error: deserializeClientError(value.error), id, ok: false };
    throw new TypeError("Invalid RPC response");
}
export function serializeRpcEvent(event) {
    return event.type === "live-query.changed"
        ? stringify({ diff: projectQueryDiff(event.diff), subscriptionId: event.subscriptionId, type: event.type })
        : stringify({ event: projectDomainEvent(event.event), subscriptionId: event.subscriptionId, type: event.type });
}
export function deserializeRpcEvent(serialized) {
    const value = parseRecord(serialized);
    const subscriptionId = stringField(value, "subscriptionId");
    if (value.type === "live-query.changed")
        return { diff: queryDiffField(value), subscriptionId, type: value.type };
    if (value.type === "kernel.event")
        return { event: domainEventField(value), subscriptionId, type: value.type };
    throw new TypeError("Unknown RPC event type");
}
function stringify(value) {
    return JSON.stringify(value);
}
function parseRecord(serialized) {
    const value = JSON.parse(serialized);
    if (!isJsonObject(value))
        throw new TypeError("Expected an RPC object");
    return value;
}
function stringField(value, field) {
    const candidate = value[field];
    if (typeof candidate !== "string")
        throw new TypeError(`Expected ${field} to be a string`);
    return candidate;
}
function jsonField(value, field) {
    const candidate = value[field];
    if (candidate === undefined)
        throw new TypeError(`Expected ${field}`);
    return candidate;
}
function queryNameField(value) {
    const name = stringField(value, "name");
    if (!isQueryName(name))
        throw new TypeError("Expected a versioned query name");
    return name;
}
function eventTypeField(value) {
    const type = stringField(value, "eventType");
    if (!isKernelEventType(type))
        throw new TypeError("Expected a known domain event type");
    return type;
}
function domainEventField(value) {
    const event = objectField(value, "event", "Expected a domain event");
    const occurredAt = timestampField(event, "occurredAt");
    const type = eventTypeField({ eventType: stringField(event, "type") });
    switch (type) {
        case "transport.state-changed":
            return { occurredAt, payload: transportStateChangedPayload(event), type };
        case "stage.catalog-invalidated":
            return { occurredAt, payload: stageCatalogInvalidatedPayload(event), type };
        case "stage.registry-changed":
            return { occurredAt, payload: stageRegistryChangedPayload(event), type };
        case "query.invalidated":
            return { occurredAt, payload: queryInvalidatedPayload(event), type };
        case "outbox.state-changed":
            return { occurredAt, payload: outboxStateChangedPayload(event), type };
        case "domain.entity-changed":
            return { occurredAt, payload: domainEntityChangedPayload(event), type };
        case "sync.delta-applied":
            return { occurredAt, payload: syncDeltaAppliedPayload(event), type };
        case "client-storage.state-changed":
            return { occurredAt, payload: clientStorageStateChangedPayload(event), type };
    }
}
function clientStorageStateChangedPayload(event) {
    const payload = objectField(event, "payload");
    const status = stringField(payload, "status");
    if (status !== "ready" && status !== "recovery-required" && status !== "starting") {
        throw new TypeError("Expected a known client-storage status");
    }
    // `code`/`message` accompany "recovery-required" only; a wire frame that omits them for
    // the other two states is well-formed, so they are decoded as optional rather than
    // required-and-empty (which would erase the distinction the shell renders on).
    const code = payload["code"];
    const message = payload["message"];
    if (code !== undefined && typeof code !== "string")
        throw new TypeError("Expected code to be a string");
    if (message !== undefined && typeof message !== "string") {
        throw new TypeError("Expected message to be a string");
    }
    return {
        ...(code === undefined ? {} : { code }),
        ...(message === undefined ? {} : { message }),
        status,
    };
}
function stageCatalogInvalidatedPayload(event) {
    const payload = objectField(event, "payload");
    return { reason: stringField(payload, "reason") };
}
function queryDiffField(value) {
    const record = objectField(value, "diff", "Expected a query diff");
    return {
        added: arrayField(record, "added"),
        deleted: arrayField(record, "deleted"),
        revision: numberField(record, "revision"),
        updated: arrayField(record, "updated"),
    };
}
function projectDomainEvent(event) {
    return { occurredAt: event.occurredAt, payload: event.payload, type: event.type };
}
function projectQueryDiff(diff) {
    return { added: diff.added, deleted: diff.deleted, revision: diff.revision, updated: diff.updated };
}
function arrayField(value, field) {
    const candidate = value[field];
    if (!Array.isArray(candidate))
        throw new TypeError(`Expected ${field} to be an array`);
    return candidate;
}
function numberField(value, field) {
    const candidate = value[field];
    if (typeof candidate !== "number" || !Number.isFinite(candidate)) {
        throw new TypeError(`Expected ${field} to be a finite number`);
    }
    return candidate;
}
function objectField(value, field, message = `Expected ${field} to be an object`) {
    const candidate = value[field];
    if (!isJsonObject(candidate))
        throw new TypeError(message);
    return candidate;
}
function stringArrayField(value, field) {
    const candidate = value[field];
    if (!Array.isArray(candidate) || !candidate.every((item) => typeof item === "string")) {
        throw new TypeError(`Expected ${field} to be an array of strings`);
    }
    return candidate;
}
function timestampField(value, field) {
    const candidate = stringField(value, field);
    const timestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/u;
    if (!timestampPattern.test(candidate) || !Number.isFinite(Date.parse(candidate))) {
        throw new TypeError(`Expected ${field} to be an RFC 3339 timestamp`);
    }
    return candidate;
}
function transportStateChangedPayload(event) {
    const payload = objectField(event, "payload");
    const state = objectField(payload, "state");
    const changedAt = timestampField(state, "changedAt");
    const path = stringField(state, "path");
    const status = stringField(state, "status");
    if (status === "connected" && isConnectedTransportPath(path)) {
        return { state: { changedAt, path, status } };
    }
    if (status === "offline" && path === "offline") {
        return { state: { changedAt, path, status } };
    }
    throw new TypeError("Expected a valid transport state");
}
function stageRegistryChangedPayload(event) {
    const payload = objectField(event, "payload");
    return { appNames: stringArrayField(payload, "appNames") };
}
function queryInvalidatedPayload(event) {
    const payload = objectField(event, "payload");
    return { name: queryNameField(payload) };
}
function outboxStateChangedPayload(event) {
    const payload = objectField(event, "payload");
    const intent = objectField(payload, "intent");
    return {
        intent: {
            domain: stringField(intent, "domain"),
            id: stringField(intent, "id"),
            idempotencyKey: stringField(intent, "idempotencyKey"),
            payload: jsonField(intent, "payload"),
            state: outboxIntentStateField(intent),
        },
    };
}
function domainEntityChangedPayload(event) {
    const payload = objectField(event, "payload");
    return {
        action: entityActionField(payload),
        entityId: stringField(payload, "entityId"),
        entityType: stringField(payload, "entityType"),
    };
}
function syncDeltaAppliedPayload(event) {
    const payload = objectField(event, "payload");
    return {
        cursor: stringField(payload, "cursor"),
        domain: stringField(payload, "domain"),
    };
}
function isConnectedTransportPath(value) {
    return (value === "iroh-relay" || value === "lan-direct" || value === "wan-direct" || value === "webrtc-direct");
}
function outboxIntentStateField(value) {
    const state = stringField(value, "state");
    if (state === "acknowledged" || state === "failed" || state === "pending" || state === "replaying") {
        return state;
    }
    throw new TypeError("Expected a known outbox intent state");
}
function entityActionField(value) {
    const action = stringField(value, "action");
    if (action === "created" || action === "deleted" || action === "updated")
        return action;
    throw new TypeError("Expected a known entity action");
}
//# sourceMappingURL=protocol.js.map